'use client';

import { useEffect } from 'react';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import type { CookieCategory } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { ConsentCategory } from '@packages/shared/src/utils/getCookieConsent/getCookieConsent';
import type { ProductListVirtualPageFragmentFragment } from '@packages/gql/generated/shopping/graphql';

/**
 * Hook for Prank A/B test OPTIN tracking.
 * Sends OPTIN: 1 outcome when marketing consent is present.
 */
export const usePRankOptinTracking = () => {
  const { abTesting } = useConfig();
  const { setOutcome } = useAbTesting();
  const { pRankTestId, odoscopeRankedTestId } = abTesting;

  const hasMarketingConsent = useCookieConsent(ConsentCategory.Marketing as CookieCategory);

  // pRank A/B test (RE_BaurDe_TransactionPrank)
  useEffect(() => {
    if (!pRankTestId) return;

    setOutcome(pRankTestId, hasMarketingConsent ? { OPTIN: 1 } : {});
  }, [pRankTestId, hasMarketingConsent, setOutcome]);

  // Odoscope Ranked A/B test (RE_BaurDe_Odoscope)
  useEffect(() => {
    if (!odoscopeRankedTestId) return;

    setOutcome(odoscopeRankedTestId, hasMarketingConsent ? { OPTIN: 1 } : {});
  }, [odoscopeRankedTestId, hasMarketingConsent, setOutcome]);
};

/**
 * Hook for Prank A/B test product tracking.
 * Marks all displayed products as test-relevant for the pRank A/B test.
 * @param items - Array of product items containing styleId
 * @param enabled - Boolean to enable or disable the hook
 */
export const usePRankProductTracking = (
  items: ProductListVirtualPageFragmentFragment['items'] | null | undefined,
  enabled = true,
) => {
  const { abTesting } = useConfig();
  const { setRelevantProduct } = useAbTesting();

  const { pRankTestId } = abTesting;

  useEffect(() => {
    if (!enabled || !pRankTestId || !items || items.length === 0) return;

    items.forEach((item) => {
      setRelevantProduct(pRankTestId, item.styleId);
    });
  }, [enabled, pRankTestId, items, setRelevantProduct]);
};
